/*
  <hv-reviews>: muro único de reseñas Loox + Revie (WhatsApp).
  Pide los datos a la sección reviews-data (Section Rendering API) cuando el
  muro se acerca a la pantalla, calcula temas y distribución con los textos
  reales, y pinta las tarjetas con las plantillas <template data-tpl> de Liquid.
*/
(() => {
  if (customElements.get('hv-reviews')) return;

  // Quita acentos pero conserva la ñ (uñas no es unas).
  const normalize = (text) =>
    (text || '')
      .toLowerCase()
      .replace(/ñ/g, '\u0001')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u0001/g, 'ñ');

  // Las palabras clave son texto simple: se quita todo lo que no sea letra, número o espacio.
  const escapeRegExp = (text) => text.replace(/[^a-z0-9ñ ]/g, '');

  const formatNumber = (n) => n.toLocaleString('es-MX');

  class HvReviews extends HTMLElement {
    connectedCallback() {
      this.wall = this.querySelector('[data-wall]');
      this.moreButton = this.querySelector('[data-more]');
      this.sortSelect = this.querySelector('[data-sort]');
      this.state = { filter: 'all', topic: null, sort: 'useful', shown: 0 };
      this.topics = Array.from(this.querySelectorAll('[data-topic]')).map((el) => ({
        el,
        // Cada palabra cuenta desde el inicio de una palabra del texto.
        keywords: (el.dataset.keywords || '')
          .split(',')
          .map((k) => normalize(k.trim()))
          .filter(Boolean)
          .map((k) => new RegExp(`(^|[^a-zñ])${escapeRegExp(k)}`)),
      }));

      this.querySelectorAll('[data-filter]').forEach((button) =>
        button.addEventListener('click', () => {
          this.state.filter = button.dataset.filter;
          this.querySelectorAll('[data-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
          this.reset();
        })
      );
      this.topics.forEach((topic) =>
        topic.el.addEventListener('click', () => {
          const active = this.state.topic === topic;
          this.state.topic = active ? null : topic;
          this.topics.forEach((t) => t.el.setAttribute('aria-pressed', String(!active && t === topic)));
          this.reset();
          if (!active && !this.topicsInRow) this.wall.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
      );
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.state.sort = this.sortSelect.value;
          this.reset();
        });
      }
      if (this.moreButton) this.moreButton.addEventListener('click', () => this.renderMore());
      this.querySelectorAll('[data-loox-write]').forEach((button) =>
        button.addEventListener('click', () => {
          // Formulario de Loox (app embed); sin él, la sección de reseñas.
          if (typeof window.looxWrite === 'function') window.looxWrite(button.dataset.looxWrite);
        })
      );

      this.media = window.matchMedia('(min-width: 990px)');
      this.mediaMid = window.matchMedia('(min-width: 600px)');
      const relayout = () => {
        this.placeTopics();
        if (this.data) this.reset(true);
      };
      this.media.addEventListener('change', relayout);
      this.mediaMid.addEventListener('change', relayout);

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '800px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      try {
        const url = new URL(window.location.href);
        url.search = '';
        url.searchParams.set('section_id', 'reviews-data');
        const html = await fetch(url).then((response) => response.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const json = doc.querySelector('[data-hv-reviews-data]');
        this.data = JSON.parse(json ? json.textContent : '[]').filter((review) => review.t || review.m.length);
      } catch (error) {
        this.data = [];
      }
      this.data.forEach((review) => {
        review.n = normalize(review.t);
        review.hasMedia = review.m.length > 0 || Boolean(review.vi);
        review.time = Date.parse(review.d) || 0;
      });
      this.classList.add('is-loaded');
      this.renderTopics();
      this.placeTopics();
      this.renderDistribution();
      this.reset();
    }

    matchesTopic(review, topic) {
      return topic.keywords.some((keyword) => keyword.test(review.n));
    }

    renderTopics() {
      const counted = this.topics.map((topic) => {
        const count = this.data.filter((review) => this.matchesTopic(review, topic)).length;
        const label = topic.el.querySelector('[data-count]');
        if (label) label.textContent = formatNumber(count);
        topic.el.hidden = count === 0;
        return { topic, count };
      });
      counted
        .sort((a, b) => b.count - a.count)
        .forEach(({ topic }) => topic.el.parentElement.appendChild(topic.el));
      this.hasTopics = counted.some(({ count }) => count > 0);
      const box = this.querySelector('[data-topics]');
      if (box) box.hidden = !this.hasTopics;
    }

    // En celular y tableta los temas viajan a la fila de filtros (deslizable);
    // en escritorio regresan a "Lo que más mencionan" junto al resumen.
    placeTopics() {
      const row = this.querySelector('[data-filter-row]');
      const box = this.querySelector('[data-topics]');
      const chips = box && box.querySelector('.hv-reviews__chips');
      if (!row || !chips || !this.topics.length) return;
      this.topicsInRow = !this.media.matches;
      const target = this.topicsInRow ? row : chips;
      // Orden actual (por conteo) antes de moverlos.
      this.topics
        .map((topic) => topic.el)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
        .forEach((el) => target.appendChild(el));
      box.hidden = this.topicsInRow || !this.hasTopics;
      requestAnimationFrame(() => this.peekRow());
    }

    // La fila de categorías debe cortar una a medias en el borde derecho:
    // si una termina justo en el borde, parece que no hay más. Busca el
    // ajuste más chico de espacio (6 a 14 px) y de relleno de cada categoría
    // (-2 a +6 px por lado) para que la del borde se asome.
    peekRow() {
      const row = this.querySelector('[data-filter-row]');
      if (!row) return;
      row.style.removeProperty('gap');
      row.style.removeProperty('--hv-chip-extra');
      if (!this.topicsInRow || row.scrollWidth <= row.clientWidth + 1) return;
      if (!this._rowObserver) {
        let width = row.clientWidth;
        this._rowObserver = new ResizeObserver(() => {
          if (row.clientWidth === width) return;
          width = row.clientWidth;
          this.peekRow();
        });
        this._rowObserver.observe(row);
      }
      const chips = Array.from(row.children).filter((el) => !el.hidden && el.getClientRects().length);
      const start = chips[0].getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
      const widths = chips.map((el) => el.getBoundingClientRect().width);
      const gap = parseFloat(getComputedStyle(row).columnGap) || 8;
      const edge = row.clientWidth;
      // La categoría del borde muestra al menos 32 px y esconde lo más que
      // se pueda: 40 %, 30 % o 20 % de su ancho, o al menos 32 px.
      const fits = (g, extra, share) => {
        let left = start;
        for (let i = 0; i < widths.length; i += 1) {
          const width = widths[i] + extra * 2;
          if (left < edge && left + width > edge) {
            return i > 0 && edge - left >= 32 && left + width - edge >= Math.max(32, width * share);
          }
          left += width + g;
        }
        return false;
      };
      let options = [];
      for (const share of [0.4, 0.3, 0.2, 0]) {
        if (fits(gap, 0, share)) return;
        for (let g = 6; g <= 14; g += 1) {
          for (let extra = -2; extra <= 6; extra += 1) {
            if (fits(g, extra, share)) options.push({ g, extra, cost: Math.abs(g - gap) + Math.abs(extra) * 1.5 });
          }
        }
        if (options.length) break;
      }
      if (!options.length) return;
      const best = options.sort((a, b) => a.cost - b.cost)[0];
      row.style.gap = `${best.g}px`;
      row.style.setProperty('--hv-chip-extra', `${best.extra}px`);
    }

    renderDistribution() {
      const box = this.querySelector('[data-distribution]');
      if (!box || !this.data.length) return;
      const counts = [0, 0, 0, 0, 0, 0];
      this.data.forEach((review) => (counts[Math.round(review.r)] += 1));
      box.querySelectorAll('[data-star]').forEach((row) => {
        const pct = Math.round((counts[Number(row.dataset.star)] / this.data.length) * 100);
        row.querySelector('[data-bar]').style.width = `${pct}%`;
        row.querySelector('[data-pct]').textContent = `${pct}%`;
      });
      box.hidden = false;
    }

    list() {
      let list = this.data;
      if (this.state.filter === 'media') list = list.filter((review) => review.hasMedia);
      if (this.state.topic) list = list.filter((review) => this.matchesTopic(review, this.state.topic));
      if (this.state.sort === 'recent') return [...list].sort((a, b) => b.time - a.time);

      // Más útiles: primero con foto, video o nota de voz y texto largo,
      // alternando tienda y WhatsApp para que el muro mezcle ambas fuentes.
      const score = (review) =>
        (review.hasMedia ? 4 : 0) + (review.au ? 3 : 0) + Math.min(review.t.length, 240) / 80 + review.r;
      const bySource = { loox: [], wa: [] };
      [...list].sort((a, b) => score(b) - score(a)).forEach((review) => bySource[review.s].push(review));
      const mixed = [];
      while (bySource.loox.length || bySource.wa.length) {
        if (bySource.loox.length) mixed.push(bySource.loox.shift());
        if (bySource.wa.length) mixed.push(bySource.wa.shift());
      }
      return mixed;
    }

    columnCount() {
      return this.media.matches ? 3 : 2;
    }

    // Muro tipo masonry: cada tarjeta va a la columna más corta.
    shortestColumn() {
      return this.columns.reduce((best, column) => (column.offsetHeight < best.offsetHeight ? column : best));
    }

    reset(keepCount) {
      const shown = keepCount ? this.state.shown : 0;
      this.filtered = this.list();
      this.state.shown = 0;
      this.wall.innerHTML = '';
      this.columns = Array.from({ length: this.columnCount() }, () => {
        const column = document.createElement('div');
        column.className = 'hv-reviews__column';
        this.wall.appendChild(column);
        return column;
      });
      const initial = Number(this.media.matches ? this.dataset.initialDesktop : this.dataset.initialMobile) || 6;
      this.renderMore(Math.max(shown, initial));
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = this.filtered.length > 0;
    }

    renderMore(amount) {
      const step = amount || Number(this.media.matches ? this.dataset.stepDesktop : this.dataset.stepMobile) || 6;
      const next = this.filtered.slice(this.state.shown, this.state.shown + step);
      next.forEach((review) => {
        const node = this.card(review);
        this.shortestColumn().appendChild(node);
        this.clamp(node);
      });
      this.state.shown += next.length;
      if (this.moreButton) this.moreButton.hidden = this.state.shown >= this.filtered.length;
    }

    // Recorta textos largos a 5 líneas con "Leer más" (solo donde el CSS recorta).
    clamp(node) {
      const text = node.querySelector('[data-slot="text"]');
      const expand = node.querySelector('[data-slot="expand"]');
      if (!text || !expand || !window.matchMedia('(max-width: 599px)').matches) return;
      node.classList.add('is-clamped');
      if (text.scrollHeight <= text.clientHeight + 1) {
        node.classList.remove('is-clamped');
        return;
      }
      expand.hidden = false;
      expand.addEventListener('click', () => {
        node.classList.remove('is-clamped');
        expand.remove();
      });
    }

    card(review) {
      const template = this.querySelector(`template[data-tpl="${review.s}"]`);
      const node = template.content.firstElementChild.cloneNode(true);
      const slot = (name) => node.querySelector(`[data-slot="${name}"]`);

      const text = slot('text');
      if (review.t) text.textContent = review.t;
      else text.remove();

      slot('author').textContent = review.a;
      const meta = slot('meta');
      if (review.s === 'loox') {
        // En celular el sello azul ya dice que es compra verificada: solo el producto.
        meta.textContent = review.p;
        if (review.v && this.dataset.verifiedLabel) {
          const label = document.createElement('span');
          label.className = 'hv-review__meta-label';
          label.textContent = `${this.dataset.verifiedLabel} `;
          meta.prepend(label);
        }
        if (!review.v) slot('verified').remove();
        node.querySelectorAll('[data-star-icon]').forEach((star, i) => star.classList.toggle('is-off', i >= review.r));
        slot('stars').setAttribute('aria-label', `${review.r} de 5 estrellas`);
      } else {
        if (review.p) meta.textContent = `${this.dataset.waLabel} ${review.p}`;
        else meta.remove();
      }

      const media = slot('media');
      const photo = review.m[0];
      if (review.vi) {
        const video = document.createElement('video');
        video.src = review.vi;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
        media.appendChild(video);
      } else if (photo && photo.t === 'video' && review.id) {
        // Loox solo publica la portada; el video vive en su reproductor.
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hv-review__video';
        button.setAttribute('aria-label', `Ver video de ${review.a}`);
        const img = document.createElement('img');
        img.src = photo.u;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        button.appendChild(img);
        const play = this.querySelector('template[data-tpl="play"]');
        if (play) button.appendChild(play.content.firstElementChild.cloneNode(true));
        button.addEventListener('click', () => window.HV && HV.openLooxReview(review.id));
        if (photo.w > 1 && photo.h > 1) media.style.aspectRatio = `1 / ${Math.min(Math.max(photo.h / photo.w, 1), 1.35)}`;
        media.appendChild(button);
      } else if (photo) {
        const img = document.createElement('img');
        img.src = photo.u;
        img.alt = `Foto de ${review.a}`;
        img.loading = 'lazy';
        img.decoding = 'async';
        if (photo.w > 1 && photo.h > 1) {
          const ratio = Math.min(Math.max(photo.h / photo.w, 1), 1.35);
          media.style.aspectRatio = `1 / ${ratio}`;
        }
        media.appendChild(img);
      } else {
        media.remove();
      }

      const voice = slot('voice');
      if (voice) {
        if (review.au) {
          const audio = voice.querySelector('audio');
          const button = voice.querySelector('button');
          audio.src = review.au;
          button.addEventListener('click', () => {
            if (audio.paused) {
              this.querySelectorAll('audio').forEach((other) => other !== audio && other.pause());
              audio.play();
            } else {
              audio.pause();
            }
          });
          audio.addEventListener('play', () => button.setAttribute('aria-pressed', 'true'));
          audio.addEventListener('pause', () => button.setAttribute('aria-pressed', 'false'));
          audio.addEventListener('ended', () => button.setAttribute('aria-pressed', 'false'));
        } else {
          voice.remove();
        }
      }
      return node;
    }
  }

  customElements.define('hv-reviews', HvReviews);
})();
